import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMeCore } from "../../../cores/me";
import { useWembiiSupportCore } from "../../../cores/wembiiSupport";
import { wembiiSupportApiService } from "../../../cores/wembiiSupport/services/api";
import {
  clearSupportConversationId,
  readSupportConversationId,
} from "../../../cores/wembiiSupport/supportSessionStorage";
import {
  pickSupportHeaderAgents,
} from "./supportChatHeaderAgents";

export type WidgetBubble = {
  id: string;
  text: string;
  from: "agent" | "user";
  senderName?: string;
  senderAvatar?: string | null;
  timestamp?: string;
  isWelcome?: boolean;
};

async function reverseGeocodeLocation(lat: number, lng: number) {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=es`,
      { headers: { Accept: "application/json" } }
    );
    if (!response.ok) return null;
    const data = await response.json();
    const address = data?.address || {};
    return {
      city:
        address.city ||
        address.town ||
        address.village ||
        address.municipality ||
        address.state ||
        "",
      country: address.country || "",
      countryCode: String(address.country_code || "").toUpperCase(),
    };
  } catch {
    return null;
  }
}

function collectSessionMetadata() {
  const nav = typeof navigator !== "undefined" ? navigator : ({} as Navigator);
  return {
    userAgent: nav.userAgent || "",
    language: nav.language || "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    browser: "",
    os: "",
    city: "",
    country: "",
    countryCode: (nav.language || "").split("-")[1]?.toUpperCase() || "",
    lat: null as number | null,
    lng: null as number | null,
  };
}

export function useWembiiSupportWidget() {
  const { user, company } = useMeCore();
  const {
    config,
    messages,
    widgetSession,
    loadConfig,
    initWidgetSession,
    endWidgetSession,
    disconnectSupportClient,
    subscribeAsClient,
    sendMessage,
    loadMessages,
    loadOnlineAgents,
    onlineAgents,
  } = useWembiiSupportCore();

  const [initialized, setInitialized] = useState(false);
  const sessionStartedRef = useRef(false);
  const widgetSessionRef = useRef(widgetSession);

  const refreshOnlineAgents = useCallback(async () => {
    try {
      await loadOnlineAgents();
    } catch {
      /* optional */
    }
  }, [loadOnlineAgents]);

  useEffect(() => {
    widgetSessionRef.current = widgetSession;
  }, [widgetSession]);

  useEffect(() => {
    if (!user?.id) return;
    loadConfig();
    refreshOnlineAgents();
  }, [loadConfig, refreshOnlineAgents, user?.id]);

  useEffect(() => {
    if (!user?.id) return undefined;

    refreshOnlineAgents();
    const timer = window.setInterval(refreshOnlineAgents, 45000);
    return () => window.clearInterval(timer);
  }, [user?.id, refreshOnlineAgents]);

  useEffect(() => {
    if (!user?.id || !company?.id || sessionStartedRef.current) return;

    const start = async () => {
      const metadata = collectSessionMetadata();

      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: false,
              maximumAge: 5 * 60 * 1000,
              timeout: 5000,
            });
          });
          metadata.lat = position.coords.latitude;
          metadata.lng = position.coords.longitude;
          const geocoded = await reverseGeocodeLocation(
            metadata.lat,
            metadata.lng
          );
          if (geocoded?.city) {
            metadata.city = geocoded.city;
            metadata.country = geocoded.country;
            metadata.countryCode = geocoded.countryCode;
          }
        } catch {
          /* optional geolocation */
        }
      }

      const session = await initWidgetSession(String(company.id), metadata);
      await loadMessages(String(session.conversationId));
      sessionStartedRef.current = true;
      setInitialized(true);
    };

    start();
  }, [user?.id, company?.id, initWidgetSession, loadMessages]);

  useEffect(() => {
    if (!user?.id || !widgetSession.conversationId || !widgetSession.room) return;
    subscribeAsClient(
      String(user.id),
      widgetSession.conversationId,
      widgetSession.room
    );
  }, [
    user?.id,
    widgetSession.conversationId,
    widgetSession.room,
    subscribeAsClient,
  ]);

  useEffect(() => {
    if (user?.id) return;

    const conversationId =
      widgetSessionRef.current.conversationId || readSupportConversationId();
    if (!conversationId) return;

    sessionStartedRef.current = false;
    setInitialized(false);
    endWidgetSession(conversationId);
  }, [user?.id, endWidgetSession]);

  useEffect(() => {
    const endActiveSession = () => {
      const conversationId =
        widgetSessionRef.current.conversationId || readSupportConversationId();
      if (!conversationId) return;
      disconnectSupportClient(conversationId);
      wembiiSupportApiService.endSession(conversationId).catch(() => undefined);
      clearSupportConversationId();
    };

    window.addEventListener("pagehide", endActiveSession);
    return () => {
      window.removeEventListener("pagehide", endActiveSession);
    };
  }, [disconnectSupportClient]);

  const welcomeMessage = config?.welcomeMessage || "Hola, ¿en qué podemos ayudarte?";

  const conversationMessages = useMemo(
    () =>
      widgetSession.conversationId
        ? messages.filter(
            (msg) => msg.conversationId === widgetSession.conversationId
          )
        : [],
    [messages, widgetSession.conversationId]
  );

  const bubbles = useMemo(() => {
    const rows: WidgetBubble[] = [
      {
        id: "welcome",
        text: welcomeMessage,
        from: "agent",
        isWelcome: true,
        senderName: "Equipo Wembii",
      },
    ];
    conversationMessages.forEach((msg) => {
      rows.push({
        id: msg.id,
        text: msg.content,
        from: msg.senderType === "user" ? "user" : "agent",
        senderName: msg.senderName,
        senderAvatar: msg.senderAvatar,
        timestamp: msg.timestamp?.time || msg.timestamp?.full,
      });
    });
    return rows;
  }, [conversationMessages, welcomeMessage]);

  const lastAgentReplierId = useMemo(() => {
    const agentMessages = conversationMessages.filter(
      (message) => message.senderType === "agent"
    );
    return agentMessages[agentMessages.length - 1]?.senderId ?? null;
  }, [conversationMessages]);

  const headerAgents = useMemo(() => {
    const agents = onlineAgents ?? [];
    if (!agents.length) return [];
    return pickSupportHeaderAgents(agents, lastAgentReplierId, 4);
  }, [lastAgentReplierId, onlineAgents]);

  useEffect(() => {
    if (!lastAgentReplierId) return;
    refreshOnlineAgents();
  }, [lastAgentReplierId, refreshOnlineAgents]);

  const sendUserMessage = useCallback(
    (text: string, consultationLabel?: string) => {
      const trimmed = text.trim();
      if (!trimmed || !widgetSession.conversationId || !user?.id) return;
      sendMessage(
        widgetSession.conversationId,
        trimmed,
        "user",
        String(user.id),
        consultationLabel
      );
    },
    [sendMessage, user?.id, widgetSession.conversationId]
  );

  return {
    config,
    bubbles,
    messages: conversationMessages,
    initialized,
    sendUserMessage,
    widgetSession,
    reloadConfig: loadConfig,
    refreshOnlineAgents,
    headerAgents,
  };
}
